/**
 * PDF Generator with Images
 * Generates PDF reports with embedded receipt images
 */

import autoTable from 'jspdf-autotable';

const FETCH_TIMEOUT_MS = 15000; // 15 second timeout per image

// Helper to load an image via fetch and convert to base64
const loadImageAsBase64 = async (url, quality = 'optimized') => {
  // Set up fetch with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  
  try {
    // For pre-signed URLs (S3/Spaces), we should NOT send Authorization headers
    // The signature is already in the URL. Adding headers can cause CORS preflight issues.
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    if (blob.size === 0) {
      throw new Error('Received empty blob');
    }
    
    // Create object URL and load into Image for processing
    const objectUrl = URL.createObjectURL(blob);
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        // Clean up object URL
        URL.revokeObjectURL(objectUrl);
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Determine dimensions based on quality
        let maxWidth, maxHeight;
        switch (quality) {
          case 'original':
            maxWidth = img.width;
            maxHeight = img.height;
            break;
          case 'thumbnail':
            maxWidth = 200;
            maxHeight = 300;
            break;
          case 'optimized':
          default:
            maxWidth = 600;
            maxHeight = 900;
            break;
        }
        
        // Calculate scaled dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', quality === 'original' ? 0.95 : 0.75);
          resolve({ dataUrl, width, height });
        } catch (err) {
          reject(new Error('Failed to convert image to data URL'));
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image into canvas'));
      };
      
      img.src = objectUrl;
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('Image fetch timed out');
    }
    throw err;
  }
};

// Helper to get effective value
const getEffectiveValue = (obj, field) => {
  const editedField = `${field}_edited`;
  return obj[editedField] && obj[editedField] !== '' ? obj[editedField] : obj[field];
};

// Format date
const formatDate = (date) => {
  if (!date) return 'No date';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid date';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
};

/**
 * Generate PDF with images
 * @param {Array} receipts - Array of receipt objects with receipt_image_uri
 * @param {Object} options - Generation options
 * @param {Function} onProgress - Progress callback (current, total, message)
 */
export const generatePDFWithImages = async (receipts, options = {}, onProgress = () => {}) => {
  const {
    layout = 'one_per_page', // 'one_per_page', 'compact', 'appendix'
    imageQuality = 'optimized', // 'original', 'optimized', 'thumbnail'
    includeLineItems = false,
    includePageNumbers = true,
    includeImageAppendix = false, // Add full-size images at the end
    items = {}, // Map of receipt uuid to items array
  } = options;

  // Dynamically import jsPDF
  const { jsPDF } = await import('jspdf');
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let currentPage = 1;
  
  // Add footer with page number and branding
  const addFooter = () => {
    const footerY = pageHeight - 10;
    doc.setFontSize(8);
    
    // Page number on left
    if (includePageNumbers) {
      doc.setTextColor(150);
      doc.text(`Page ${currentPage}`, margin, footerY);
    }
    
    // Branding on right - "Exported from" in gray, "myreceipts.ai" in blue with underline
    const prefix = 'Exported from ';
    const linkText = 'myreceipts.ai';
    const linkWidth = doc.getTextWidth(linkText);
    const prefixWidth = doc.getTextWidth(prefix);
    const totalWidth = prefixWidth + linkWidth;
    const startX = pageWidth - margin - totalWidth;
    
    // "Exported from" in gray
    doc.setTextColor(150);
    doc.text(prefix, startX, footerY);
    
    // "myreceipts.ai" in blue with underline (clickable)
    doc.setTextColor(59, 130, 246); // Blue color
    const linkX = startX + prefixWidth;
    doc.textWithLink(linkText, linkX, footerY, { url: 'https://myreceipts.ai' });
    
    // Draw underline
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.3);
    doc.line(linkX, footerY + 1, linkX + linkWidth, footerY + 1);
    
    doc.setTextColor(0);
    doc.setDrawColor(0);
  };

  // Add new page with footer
  const addNewPage = () => {
    addFooter();
    doc.addPage();
    currentPage++;
    return margin;
  };

  // Calculate totals for summary
  const totalAmount = receipts.reduce((sum, r) => {
    const cost = parseFloat(getEffectiveValue(r, 'total_cost') || 0);
    return sum + (isNaN(cost) ? 0 : cost);
  }, 0);

  // ============ TITLE PAGE ============
  let yPosition = margin;
  
  // Title
  doc.setFontSize(24);
  doc.setFont(undefined, 'bold');
  doc.text('Receipt Export Report', pageWidth / 2, yPosition + 20, { align: 'center' });
  
  yPosition += 40;
  
  // Summary box
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.setDrawColor(200);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 50, 3, 3, 'FD');
  
  yPosition += 15;
  doc.text(`Total Receipts: ${receipts.length}`, margin + 10, yPosition);
  yPosition += 10;
  doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, margin + 10, yPosition);
  yPosition += 10;
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin + 10, yPosition);
  
  yPosition += 30;
  
  // Date range
  const dates = receipts
    .map(r => new Date(getEffectiveValue(r, 'date')))
    .filter(d => !isNaN(d.getTime()))
    .sort((a, b) => a - b);
  
  if (dates.length > 0) {
    doc.setFontSize(10);
    doc.text(`Date Range: ${formatDate(dates[0])} - ${formatDate(dates[dates.length - 1])}`, margin + 10, yPosition);
  }

  // Load images in parallel with progress
  onProgress(0, receipts.length, 'Loading receipt images...');
  
  const imagePromises = receipts.map(async (receipt, index) => {
    if (receipt.receipt_image_uri && !receipt.mime_type?.startsWith('application/pdf')) {
      try {
        const imageData = await loadImageAsBase64(receipt.receipt_image_uri, imageQuality);
        onProgress(index + 1, receipts.length, `Loading images... ${index + 1}/${receipts.length}`);
        return { uuid: receipt.uuid, ...imageData };
      } catch (err) {
        return { uuid: receipt.uuid, error: true, errorMessage: err.message };
      }
    }
    return { uuid: receipt.uuid, error: true, isPdf: receipt.mime_type?.startsWith('application/pdf') };
  });

  const loadedImages = await Promise.all(imagePromises);
  const imageMap = new Map(loadedImages.map(img => [img.uuid, img]));

  onProgress(0, receipts.length, 'Generating PDF...');

  // Helper to draw receipt header with optional thumbnail
  const drawReceiptHeader = (receipt, imageData, startY) => {
    let y = startY;
    const thumbnailWidth = 70;
    const thumbnailHeight = 100;
    const hasImage = imageData && !imageData.error;
    const isPdfReceipt = imageData?.isPdf;
    // Always reserve space for thumbnail area, show text to the right
    const textStartX = margin + thumbnailWidth + 12;
    
    // Draw thumbnail on the left if available
    if (hasImage) {
      // Add subtle border around thumbnail
      doc.setDrawColor(220);
      doc.rect(margin - 0.5, y - 0.5, thumbnailWidth + 1, thumbnailHeight + 1);
      doc.addImage(imageData.dataUrl, 'JPEG', margin, y, thumbnailWidth, thumbnailHeight);
    } else {
      // Placeholder box with appropriate message
      doc.setDrawColor(200);
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, y, thumbnailWidth, thumbnailHeight, 'FD');
      doc.setFontSize(8);
      doc.setTextColor(140);
      if (isPdfReceipt) {
        doc.text('PDF Receipt', margin + 13, y + 48);
        doc.setFontSize(7);
        doc.text('(image N/A)', margin + 16, y + 56);
      } else {
        doc.text('No image', margin + 17, y + 52);
      }
      doc.setTextColor(0);
    }
    
    // Merchant name (larger, bold)
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text(getEffectiveValue(receipt, 'merchant_name') || 'Unknown Merchant', textStartX, y + 12);
    
    // Date (closer to merchant name)
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(120);
    doc.text(formatDate(getEffectiveValue(receipt, 'date')), textStartX, y + 22);
    doc.setTextColor(0);
    
    // Total cost (prominent)
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(`Total: ${formatCurrency(getEffectiveValue(receipt, 'total_cost'))}`, textStartX, y + 38);
    doc.setFont(undefined, 'normal');
    
    // Tax and tip on same line if both present, otherwise stacked
    const tax = getEffectiveValue(receipt, 'tax');
    const tip = getEffectiveValue(receipt, 'tip');
    const hasTax = tax && parseFloat(tax) > 0;
    const hasTip = tip && parseFloat(tip) > 0;
    
    if (hasTax || hasTip) {
      doc.setFontSize(9);
      doc.setTextColor(120);
      let taxTipText = '';
      if (hasTax) taxTipText += `Tax: ${formatCurrency(tax)}`;
      if (hasTax && hasTip) taxTipText += '  •  ';
      if (hasTip) taxTipText += `Tip: ${formatCurrency(tip)}`;
      doc.text(taxTipText, textStartX, y + 50);
      doc.setTextColor(0);
    }
    
    return y + thumbnailHeight + 8;
  };

  // ============ LAYOUT: ONE PER PAGE ============
  if (layout === 'one_per_page') {
    for (let i = 0; i < receipts.length; i++) {
      const receipt = receipts[i];
      onProgress(i + 1, receipts.length, `Adding receipt ${i + 1}/${receipts.length}...`);
      
      yPosition = addNewPage();
      const imageData = imageMap.get(receipt.uuid);
      
      // Draw header with thumbnail and receipt info
      yPosition = drawReceiptHeader(receipt, imageData, yPosition);
      
      // Full receipt image (if not showing line items, show larger image)
      if (!includeLineItems && imageData && !imageData.error) {
        const maxImgWidth = pageWidth - 2 * margin;
        const maxImgHeight = pageHeight - yPosition - 30;
        
        let imgWidth = imageData.width;
        let imgHeight = imageData.height;
        
        const scale = Math.min(maxImgWidth / imgWidth, maxImgHeight / imgHeight, 1);
        imgWidth *= scale;
        imgHeight *= scale;
        
        const imgX = (pageWidth - imgWidth) / 2;
        doc.addImage(imageData.dataUrl, 'JPEG', imgX, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 10;
      } else if (!includeLineItems) {
        // No line items and no/error image - show message
        if (imageData?.isPdf) {
          doc.setFontSize(10);
          doc.setTextColor(150);
          doc.text('(Original receipt is a PDF - image not included)', margin, yPosition);
          doc.setTextColor(0);
        }
        yPosition += 10;
      }
      
      // Line items table if requested
      if (includeLineItems && items[receipt.uuid]) {
        const receiptItems = items[receipt.uuid];
        if (receiptItems.length > 0) {
          // Check if we need a new page for the table
          if (yPosition > pageHeight - 60) {
            yPosition = addNewPage();
            // Re-draw header on new page for context
            yPosition = drawReceiptHeader(receipt, imageData, yPosition);
          }
          
          // Build table data from line items
          const tableBody = receiptItems.map(item => {
            const itemName = getEffectiveValue(item, 'item_name') || 'Unknown Item';
            const category = getEffectiveValue(item, 'high_level_category') || '—';
            const itemPrice = parseFloat(getEffectiveValue(item, 'item_price')) || 0;
            const isDiscount = item.is_maybe_discount;
            
            // For discounts, show as negative
            const displayPrice = isDiscount ? -Math.abs(itemPrice) : itemPrice;
            
            return [
              isDiscount ? `${itemName} (Discount)` : itemName,
              isDiscount ? 'Discount' : (category || '—'),
              formatCurrency(displayPrice)
            ];
          });
          
          // Add tax as a line item if present
          const tax = getEffectiveValue(receipt, 'maybe_receipt_tax');
          if (tax && tax !== '0' && tax !== '' && parseFloat(tax) > 0) {
            tableBody.push([
              'Tax',
              'Tax',
              formatCurrency(tax)
            ]);
          }
          
          // Add tip as a line item if present
          const tip = getEffectiveValue(receipt, 'maybe_receipt_tip');
          if (tip && tip !== '0' && tip !== '' && parseFloat(tip) > 0) {
            tableBody.push([
              'Tip',
              'Tip',
              formatCurrency(tip)
            ]);
          }
          
          // Calculate total (items - discounts + tax + tip)
          const itemsSubtotal = receiptItems.reduce((sum, item) => {
            const price = parseFloat(getEffectiveValue(item, 'item_price')) || 0;
            // Subtract discounts, add regular items
            return item.is_maybe_discount ? sum - price : sum + price;
          }, 0);
          const totalWithTaxTip = itemsSubtotal + (parseFloat(tax) || 0) + (parseFloat(tip) || 0);
          
          autoTable(doc, {
            startY: yPosition,
            head: [['Item', 'Category', 'Price']],
            body: tableBody,
            foot: [[
              { content: 'Total', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
              { content: formatCurrency(totalWithTaxTip), styles: { fontStyle: 'bold' } }
            ]],
            theme: 'striped',
            styles: { 
              fontSize: 8,
              cellPadding: 2,
            },
            headStyles: { 
              fillColor: [66, 66, 66],
              textColor: [255, 255, 255],
              fontStyle: 'bold',
            },
            footStyles: {
              fillColor: [240, 240, 240],
              textColor: [0, 0, 0],
            },
            columnStyles: {
              0: { cellWidth: 'auto' }, // Item name - flexible
              1: { cellWidth: 40 }, // Category
              2: { cellWidth: 30, halign: 'right' }, // Price
            },
            margin: { left: margin, right: margin },
            tableWidth: pageWidth - 2 * margin,
            didDrawPage: (data) => {
              // Update current page count when autotable adds pages
              currentPage = doc.internal.getNumberOfPages();
            }
          });
          
          yPosition = doc.lastAutoTable.finalY + 10;
        }
      }
    }
  }
  
  // ============ LAYOUT: COMPACT ============
  else if (layout === 'compact') {
    yPosition = addNewPage();
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Receipts', margin, yPosition);
    yPosition += 10;
    
    for (let i = 0; i < receipts.length; i++) {
      const receipt = receipts[i];
      onProgress(i + 1, receipts.length, `Adding receipt ${i + 1}/${receipts.length}...`);
      
      // Check if we need a new page
      if (yPosition > pageHeight - 80) {
        yPosition = addNewPage();
      }
      
      const rowHeight = 50;
      const thumbnailSize = 40;
      
      // Thumbnail
      const imageData = imageMap.get(receipt.uuid);
      if (imageData && !imageData.error) {
        doc.addImage(imageData.dataUrl, 'JPEG', margin, yPosition, thumbnailSize, thumbnailSize);
      } else {
        // Placeholder box
        doc.setDrawColor(200);
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPosition, thumbnailSize, thumbnailSize, 'FD');
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('No img', margin + 8, yPosition + 25);
        doc.setTextColor(0);
      }
      
      // Receipt info
      const textX = margin + thumbnailSize + 10;
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(getEffectiveValue(receipt, 'merchant_name') || 'Unknown', textX, yPosition + 12);
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      doc.text(formatDate(getEffectiveValue(receipt, 'date')), textX, yPosition + 22);
      doc.setTextColor(0);
      
      // Amount on right
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      const amount = formatCurrency(getEffectiveValue(receipt, 'total_cost'));
      doc.text(amount, pageWidth - margin, yPosition + 12, { align: 'right' });
      doc.setFont(undefined, 'normal');
      
      // Divider line
      yPosition += rowHeight;
      doc.setDrawColor(230);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 5;
    }
  }
  
  // ============ LAYOUT: APPENDIX ============
  else if (layout === 'appendix') {
    // First, add data summary pages
    yPosition = addNewPage();
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Receipt Summary', margin, yPosition);
    yPosition += 10;
    
    // Table header
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
    doc.text('Date', margin + 2, yPosition + 5);
    doc.text('Merchant', margin + 30, yPosition + 5);
    doc.text('Amount', pageWidth - margin - 25, yPosition + 5);
    yPosition += 10;
    
    doc.setFont(undefined, 'normal');
    
    for (let i = 0; i < receipts.length; i++) {
      const receipt = receipts[i];
      
      if (yPosition > pageHeight - 20) {
        yPosition = addNewPage();
        // Repeat header
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
        doc.text('Date', margin + 2, yPosition + 5);
        doc.text('Merchant', margin + 30, yPosition + 5);
        doc.text('Amount', pageWidth - margin - 25, yPosition + 5);
        yPosition += 10;
        doc.setFont(undefined, 'normal');
      }
      
      doc.setFontSize(8);
      doc.text(formatDate(getEffectiveValue(receipt, 'date')), margin + 2, yPosition + 4);
      
      let merchantName = getEffectiveValue(receipt, 'merchant_name') || 'Unknown';
      if (merchantName.length > 35) merchantName = merchantName.substring(0, 35) + '...';
      doc.text(merchantName, margin + 30, yPosition + 4);
      
      doc.text(formatCurrency(getEffectiveValue(receipt, 'total_cost')), pageWidth - margin - 25, yPosition + 4);
      
      yPosition += 7;
    }
    
    // Add totals
    yPosition += 5;
    doc.setDrawColor(0);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Total:', margin + 30, yPosition);
    doc.text(formatCurrency(totalAmount), pageWidth - margin - 25, yPosition);
    
    // Now add images appendix
    yPosition = addNewPage();
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Receipt Images', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;
    
    for (let i = 0; i < receipts.length; i++) {
      const receipt = receipts[i];
      onProgress(i + 1, receipts.length, `Adding images ${i + 1}/${receipts.length}...`);
      
      const imageData = imageMap.get(receipt.uuid);
      
      if (imageData && !imageData.error) {
        if (yPosition > margin + 10) {
          yPosition = addNewPage();
        }
        
        // Label
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`${getEffectiveValue(receipt, 'merchant_name')} - ${formatDate(getEffectiveValue(receipt, 'date'))}`, margin, yPosition);
        yPosition += 8;
        
        // Image
        const maxImgWidth = pageWidth - 2 * margin;
        const maxImgHeight = pageHeight - yPosition - 20;
        
        let imgWidth = imageData.width;
        let imgHeight = imageData.height;
        
        const scale = Math.min(maxImgWidth / imgWidth, maxImgHeight / imgHeight, 1);
        imgWidth *= scale;
        imgHeight *= scale;
        
        const imgX = (pageWidth - imgWidth) / 2;
        doc.addImage(imageData.dataUrl, 'JPEG', imgX, yPosition, imgWidth, imgHeight);
      }
    }
  }

  // ============ IMAGE APPENDIX ============
  if (includeImageAppendix) {
    // Add appendix title page
    yPosition = addNewPage();
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Receipt Images', pageWidth / 2, yPosition + 10, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100);
    doc.text('Full-size receipt images for reference', pageWidth / 2, yPosition + 20, { align: 'center' });
    doc.setTextColor(0);
    
    // Add each receipt image on its own page
    for (let i = 0; i < receipts.length; i++) {
      const receipt = receipts[i];
      const imageData = imageMap.get(receipt.uuid);
      
      if (imageData && !imageData.error) {
        onProgress(i + 1, receipts.length, `Adding appendix image ${i + 1}/${receipts.length}...`);
        
        yPosition = addNewPage();
        
        // Receipt label at top
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(getEffectiveValue(receipt, 'merchant_name') || 'Unknown Merchant', margin, yPosition);
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100);
        doc.text(
          `${formatDate(getEffectiveValue(receipt, 'date'))} • ${formatCurrency(getEffectiveValue(receipt, 'total_cost'))}`,
          margin,
          yPosition + 6
        );
        doc.setTextColor(0);
        
        yPosition += 15;
        
        // Full-size image
        const maxImgWidth = pageWidth - 2 * margin;
        const maxImgHeight = pageHeight - yPosition - 20;
        
        let imgWidth = imageData.width;
        let imgHeight = imageData.height;
        
        const scale = Math.min(maxImgWidth / imgWidth, maxImgHeight / imgHeight, 1);
        imgWidth *= scale;
        imgHeight *= scale;
        
        const imgX = (pageWidth - imgWidth) / 2;
        doc.addImage(imageData.dataUrl, 'JPEG', imgX, yPosition, imgWidth, imgHeight);
      }
    }
  }

  // Add final footer
  addFooter();
  
  // Save
  const filename = `receipt_export_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
  
  return { success: true, filename };
};

export default generatePDFWithImages;

